/** React Query cache keys — keep in sync anywhere email-lead data is fetched. */
export const EMAIL_LEAD_SETTINGS_QUERY_KEY = ['emailLeadSettings'];
export const EMAIL_LEAD_SOURCES_QUERY_KEY = ['emailLeadSources'];
export const EMAIL_LEAD_SUGGESTIONS_QUERY_KEY = ['emailLeadSuggestions'];
export const EMAIL_LEAD_REUSABLE_SENDERS_QUERY_KEY = ['emailLeadReusableSenders'];

export const emailLeadMessagesQueryKey = (params) => ['emailLeadMessages', params];
export const emailLeadMessageQueryKey = (id) => ['emailLeadMessage', id];

/**
 * Status vocabulary for the ingested-email list.
 *
 * `tone` drives the chip colour. The ordering below is the order the filter
 * chips appear in, and it is not alphabetical on purpose: "needs review" comes
 * first because it is the only status that is asking the user for something.
 */
export const EMAIL_LEAD_STATUSES = [
  {
    value: 'needs_review',
    label: 'Needs review',
    tone: 'amber',
    hint: 'An enquiry we could not finish reading. Add the missing detail and it becomes a lead.'
  },
  {
    value: 'ingested',
    label: 'Lead created',
    tone: 'green',
    hint: 'A pre-lead was created from this email.'
  },
  {
    value: 'duplicate',
    label: 'Already in list',
    tone: 'gray',
    hint: 'This number was already in the target pre-lead list, so the enquiry was folded into it.'
  },
  {
    value: 'skipped',
    label: 'Not a lead',
    tone: 'gray',
    hint: 'Marketing mail, an invoice, or a portal digest with no contact details in it.'
  },
  {
    value: 'failed',
    label: 'Failed',
    tone: 'red',
    hint: 'Something went wrong reading this email. Re-process it once the cause is fixed.'
  },
  { value: 'pending', label: 'Pending', tone: 'gray', hint: 'Fetched, not yet parsed.' }
];

export function emailLeadStatusMeta(status) {
  return (
    EMAIL_LEAD_STATUSES.find((entry) => entry.value === status) ?? {
      value: status,
      label: status ?? 'Unknown',
      tone: 'gray',
      hint: ''
    }
  );
}

const TONE_CLASSES = {
  green: 'bg-green-50 text-green-800 ring-green-200',
  amber: 'bg-amber-50 text-amber-900 ring-amber-200',
  red: 'bg-red-50 text-red-800 ring-red-200',
  gray: 'bg-gray-100 text-gray-700 ring-gray-200'
};

export function emailLeadStatusClass(status) {
  return TONE_CLASSES[emailLeadStatusMeta(status).tone] ?? TONE_CLASSES.gray;
}

/**
 * A dialable number for the list and the detail screen.
 *
 * The country code is kept separate on the row because that is how pre_leads
 * stores it, but a national number alone is not something a rep can act on —
 * "556560470" means nothing without the +971 in front of it, and overseas
 * enquiries are common on these portals.
 */
export function formatContactPhone(row) {
  const national = String(row?.contactPhone ?? '').trim();
  if (!national) return null;
  const code = String(row?.contactCountryCode ?? '').trim();
  return code ? `${code} ${national}` : national;
}

/** At least one mailbox connected and not paused. */
export function hasActiveMailbox(settings) {
  return (settings?.mailboxes ?? []).some((mailbox) => mailbox.isActive);
}

/**
 * A mailbox the builder has to do something about.
 *
 * Paused counts: the poller parks a mailbox after repeated failures, which is
 * silent otherwise — the leads simply stop, and nothing on screen says why.
 */
export function mailboxesNeedingAttention(settings) {
  return (settings?.mailboxes ?? []).filter(
    (mailbox) => !mailbox.isActive || Boolean(mailbox.lastError)
  );
}

export function emailLeadsStatusLabel(settings, { isPending, isError } = {}) {
  if (isPending) return 'Checking…';
  if (isError) return 'Connect';

  const mailboxes = settings?.mailboxes ?? [];
  if (mailboxes.length === 0) return 'Connect inbox';
  if (mailboxesNeedingAttention(settings).length > 0) return 'Needs attention';
  if (!mailboxes.some((mailbox) => mailbox.hasSynced)) return 'Finishing setup';

  return mailboxes.length === 1 ? '1 inbox connected' : `${mailboxes.length} inboxes connected`;
}

/**
 * Turn a sync run's counts into one sentence.
 *
 * Written out rather than shown as a row of numbers because the counts only
 * mean something in combination: "20 read, 0 created" is alarming, "20 read,
 * 20 already in list" is not, and a bare "20" says neither.
 */
export function describeSyncResult(stats) {
  if (!stats) return 'Sync finished';
  if (!stats.fetched) return 'No new emails in this inbox';

  const parts = [];
  if (stats.created) parts.push(`${stats.created} new lead${stats.created === 1 ? '' : 's'}`);
  if (stats.needs_review) parts.push(`${stats.needs_review} needing review`);
  if (stats.duplicates) parts.push(`${stats.duplicates} already in your lists`);
  if (stats.skipped) parts.push(`${stats.skipped} not leads`);
  if (stats.failed) parts.push(`${stats.failed} failed`);

  const read = `Read ${stats.fetched} email${stats.fetched === 1 ? '' : 's'}`;
  return parts.length ? `${read} — ${parts.join(', ')}` : read;
}
